import type { Occasion } from '../types';
import { ROUTES } from './routes';

/** Constants for the API */
export const HOSTNAME =
  process.env.REACT_APP_SERVER_API_URL || `http://localhost:8080`;
export const API_PATH = `${HOSTNAME}/api/v1`;
export const ENV = process.env.REACT_APP_ENV || 'development';
export const IS_PRODUCTION = ENV === 'production';
export const IS_DEVELOPMENT = ENV === 'development';
export const CONTENT_TYPE = `Content-Type`;
export const APPLICATION_JSON = `application/json`;
export const AUTHORIZATION = `Authorization`;
export const BEARER = `Bearer`;
export const TOKEN = `token`;

/** Constants for server side endpoints */
export const HEALTH = `/health`;
export const TRIPS = `/trips`;
export const SHARE = '/share';
export const SHARED = '/shared';
export const ENABLE = '/enable';
export const REVOKE = '/revoke';
export const ROTATE = '/rotate';
export const MEMBERSHIPS = `/memberships`;
export const VISITS = `/visits`;
export const LOCATIONS = `/locations`;
export const SEARCH = `/search`;
export const AUTH = `/auth`;
export const SIGNIN = `/signin`;
export const SIGNUP = `/signup`;
export const ME = `/me`;
export const USER = `/user`;
export const USERS = `/users`;
export const TRAVELERS = `/travelers`;

/** Constants for the pagination */
export const DEFAULT_PAGE_NUMBER = 0;
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 50;

/** Minimum trimmed query length before prefix search hooks call the API (matches server SEARCH_MIN_LENGTH). */
export const SEARCH_MIN_LENGTH = 2;

/** Constants for the user */
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 100;
export const LOCALE = 'locale';

/** Constants for the username */
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 50;

/** Constants for the email */
export const EMAIL_MAX_LENGTH = 40;

/** Constants for the password */
export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_MAX_LENGTH = 100;

/** Error types for different HTTP status codes */
export enum ErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/** HTTP status constants */
export enum HttpStatus {
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
}

/** Public share URL for display and clipboard copy. */
export function buildShareUrl(shareToken: string | null | undefined): string {
  if (!shareToken) {
    return '';
  }
  return `${getShareLinkOrigin()}${ROUTES.sharedTrip(shareToken)}`;
}

/** Where the client is loaded (not REACT_APP_ENV). */
function getShareLinkOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
}

export const OCCASION_LABELS: Record<string, string> = {
  HONEYMOON: 'Honeymoon',
  ANNIVERSARY: 'Anniversary',
  BABYMOON: 'Babymoon',
  BACHELORETTE: 'Bachelorette',
  BACHELOR: 'Bachelor',
  FAMILY: 'Family',
  CAMPING: 'Camping',
  ROAD_TRIP: 'Road Trip',
  GRADUATION: 'Graduation',
  BIRTHDAY: 'Birthday',
  RETIREMENT: 'Retirement',
  BUSINESS: 'Business',
  OTHER: 'Other',
};

export const OCCASIONS: Occasion[] = [
  'HONEYMOON',
  'ANNIVERSARY',
  'BABYMOON',
  'BACHELORETTE',
  'BACHELOR',
  'FAMILY',
  'CAMPING',
  'ROAD_TRIP',
  'GRADUATION',
  'BIRTHDAY',
  'RETIREMENT',
  'BUSINESS',
  'OTHER',
];

export const STATUS_LABELS: Record<string, string> = {
  PLANNING: 'Planning',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const VISIBILITY_LABELS: Record<string, string> = {
  CREATOR: 'Creator only',
  ADMINS: 'Admins only',
  MEMBERS: 'All members',
  PUBLIC: 'Public',
};
